import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Header,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Box,
  Shimmer,
} from "@repo/ui";
import { useTheme, type SubjectColorKey } from "@repo/tokens";
import {
  useGetExamtocGetVideosForExamChapterSubtenantAndVideoTypeExamIdChapterIdSubtenantId,
  useGetApiSelflearnGetVideoDetailsForPlayListTypeId,
} from "@oneapp/data";
import styles from "./ReviseByVideosViewAllPage.module.scss";
import type { Video, VideoProgress } from "../../../../types/reviseTypes";
import { REVISE_VIDEOS_PLAYLIST_TYPE_ID } from "../revisePlaylistConstants";
import { SELF_LEARN_ROUTES } from "../../../../routes";
import { formatDurationVerbose } from "../../../../lib/formatUtils";
import { ROUTESWEB, useIsMobile } from "@repo/utils";
import { defaultSelfLearnSession } from "../../../../lib/selfLearnSession";
import type { VideoPlayerBreadcrumbItem } from "../../../selflearn/SelfLearnVideoPlayerPage";

const SELF_LEARN_BASE = `/${ROUTESWEB.APP.SELF_LEARN}`;
const CHAPTER_LIST_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}`;
const CHAPTER_DETAILS_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}/${SELF_LEARN_ROUTES.CHAPTERDETAILS}`;
const reviseByVideosViewAllHref = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}/${SELF_LEARN_ROUTES.CHAPTERDETAILS}/${SELF_LEARN_ROUTES.REVISE_VIDEOS_VIEW_ALL}`;

export type ReviseByVideosViewAllLocationState = {
  subjectId: number;
  subjectName: string;
  chapterId: number;
  chapterName: string;
  widgetTitle: string;
  videoTypeIds: number[];
};

const LoadingSkeleton = ({
  subjectName,
  ariaLabel,
}: {
  subjectName: string;
  ariaLabel: string;
}) => {
  const { theme } = useTheme();
  const subjectColor =
    theme.colors.subject[subjectName as SubjectColorKey] ??
    theme.colors.primary;
  const baseColor = subjectColor[95];
  const highlightColor = subjectColor[100];

  return (
    <div
      className={styles.listContainer}
      role="status"
      aria-label={ariaLabel}
      aria-busy="true"
    >
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className={styles.shimmerItem}>
          <Shimmer
            variant="card"
            width={89}
            height={56}
            shape="rounded"
            borderRadius={8}
            baseColor={baseColor}
            highlightColor={highlightColor}
            style={{ flexShrink: 0 }}
          />
          <div className={styles.shimmerContent}>
            <Shimmer
              variant="textLine"
              width="70%"
              height={16}
              shape="rounded"
              baseColor={baseColor}
              highlightColor={highlightColor}
              borderRadius={4}
            />
            <Shimmer
              variant="textLine"
              width="40%"
              height={12}
              shape="rounded"
              baseColor={baseColor}
              highlightColor={highlightColor}
              borderRadius={4}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export type AvatarItem = {
  id: number;
  label: string;
  onPress?: () => void;
};

export const ReviseByVideosViewAllPage = () => {
  const { t } = useTranslation("selflearn");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();

  const avatarItems = useMemo(
    (): AvatarItem[] => [
      { id: 1, label: tc("profileMenu.myProfile") },
      { id: 2, label: tc("profileMenu.switchProfile") },
      { id: 3, label: tc("profileMenu.switchProduct") },
    ],
    [tc],
  );
  const {
    subjectId,
    subjectName,
    chapterId,
    chapterName,
    widgetTitle,
    videoTypeIds,
  } = (location.state as ReviseByVideosViewAllLocationState | null) ?? {};

  const selfLearnBasePath = useMemo(
    () => `/${ROUTESWEB.APP.SELF_LEARN}`.replace(/\/$/, ""),
    [],
  );

  const videoPlayerPath = useMemo(
    () =>
      `${selfLearnBasePath}/${SELF_LEARN_ROUTES.VIDEO_PLAYER}`.replace(
        /\/$/,
        "",
      ),
    [selfLearnBasePath],
  );

  const subjectCrumb = subjectName ?? "";
  const chapterCrumb = chapterName ?? "";

  const gradeId = defaultSelfLearnSession?.gradeId ?? 0;
  const subtenantId = defaultSelfLearnSession?.subtenantId ?? 0;
  const examId = defaultSelfLearnSession?.examId ?? 0;
  const isDesktopLayout = !useIsMobile(1024);

  const {
    data: videosResponse,
    isLoading,
    isError,
  } = useGetExamtocGetVideosForExamChapterSubtenantAndVideoTypeExamIdChapterIdSubtenantId(
    examId,
    chapterId ?? 0,
    subtenantId,
    { videoTypeIds: videoTypeIds ?? [] },
    {
      request: { clientName: "cms", paramsSerializer: { indexes: null } },
      query: {
        select: (data) => data.data as Video[],
        refetchOnMount: true,
        staleTime: 0,
      },
    },
  );

  const { data: progressVideos } =
    useGetApiSelflearnGetVideoDetailsForPlayListTypeId(
      { examId, playlistTypeId: REVISE_VIDEOS_PLAYLIST_TYPE_ID, gradeId },
      {
        request: { clientName: "selflearn" },
        query: {
          refetchOnWindowFocus: true,
          select: (data) => data.videos as VideoProgress[],
          refetchOnMount: true,
          staleTime: 0,
        },
      },
    );

  const videos = useMemo((): Video[] => {
    if (!videosResponse) return [];
    const progressMap = new Map<number, VideoProgress>(
      progressVideos?.map((p) => [p.videoId, p]) ?? [],
    );
    return videosResponse.map((video): Video => {
      const progress = progressMap.get(video.videoId);
      return {
        ...video,
        watchedContentInSecs: progress?.watchedContentInSecs ?? 0,
        videoUrl: video.videoURL,
        thumbnail: video.thumbnailURL,
        isCompleted: progress?.isCompleted ?? video.isCompleted,
        isBookmarked: progress?.isBookmarked ?? video.isBookmarked,
        isLiked: progress?.isLiked ?? video.isLiked,
        isDisLiked: progress?.isDisLiked ?? video.isDisLiked,
        completedDate: progress?.completedDate ?? video.completedDate,
        bookmarkedDate: progress?.bookmarkedDate ?? video.bookmarkedDate,
      };
    });
  }, [videosResponse, progressVideos]);

  const openVideoPlayer = useCallback(
    (index: number) => {
      if (index < 0 || index >= videos.length) return;
      const list = videos.map((v) => ({
        ...v,
        subjectId: subjectId ?? 0,
      }));
      navigate(
        `${videoPlayerPath}?_=${encodeURIComponent(String(Date.now()))}`,
        {
          state: {
            title: widgetTitle,
            item: list[index],
            videos: list,
            index,
            playlistId: REVISE_VIDEOS_PLAYLIST_TYPE_ID,
            breadcrumbItems: [
              {
                label: "Learn",
                mode: "replace",
                delta: -1,
                to: SELF_LEARN_BASE,
              },
              {
                label: subjectName,
                mode: "replace",
                delta: -1,
                to: CHAPTER_LIST_HREF,
                state: {
                  subjectId,
                  subjectName,
                },
              },
              {
                label: chapterName,
                mode: "replace",
                to: CHAPTER_DETAILS_HREF,
                state: {
                  chapterId,
                  chapterName,
                  subjectId,
                  subjectName,
                  initialTab: "revise",
                },
              },
              {
                label: "Revise By Videos",
                mode: "back",
                delta: -1,
                to: reviseByVideosViewAllHref,
              },
              {
                label: list[index].title,
                isCurrent: true,
              },
            ] as VideoPlayerBreadcrumbItem[],
          },
        },
      );
    },
    [navigate, videos, subjectId, videoPlayerPath, widgetTitle],
  );

  const handleSubjectPress = () => {
    navigate(CHAPTER_LIST_HREF, {
      replace: true,
      state: {
        subjectId,
        subjectName,
      },
    });
    return;
  };

  const handleChapterPress = () => {
    navigate(CHAPTER_DETAILS_HREF, {
      replace: true,
      state: {
        chapterId: chapterId,
        chapterName: chapterName,
        subjectId,
        subjectName,
        initialTab: "revise",
      },
    });
    return;
  };

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbItem key="learn">
          <BreadcrumbLink
            href={SELF_LEARN_BASE}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              navigate(SELF_LEARN_BASE, { replace: true });
            }}
          >
            {t("learnText")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-1" />
        <BreadcrumbItem key="subject">
          <BreadcrumbLink
            href={CHAPTER_LIST_HREF}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleSubjectPress();
            }}
          >
            {subjectCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-2" />
        <BreadcrumbItem key="chapter-details">
          <BreadcrumbLink
            href={CHAPTER_DETAILS_HREF}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleChapterPress();
            }}
          >
            {chapterCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-3" />
        <BreadcrumbItem key="revise-videos-all" isCurrentPage>
          <BreadcrumbLink isActive>
            {widgetTitle ?? t("revise.reviseByVideos.viewAllTitleFallback")}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    ),
    [navigate, subjectCrumb, chapterCrumb, widgetTitle, t],
  );

  return (
    <div className={styles.page}>
      <Box className={styles.headerWrapper}>
        <div className={styles.headerInner}>
          <Header
            title={isDesktopLayout ? undefined : widgetTitle}
            leftVariant="back"
            backIcon="circleBackBtn"
            backIconShadow={true}
            backIconWidth={48}
            backIconHeight={48}
            rightIcon={isDesktopLayout ? "userProfileIcon" : undefined}
            rightIconItems={avatarItems}
            onBackPress={() => {
              navigate(-1);
            }}
            breadcrumb={isDesktopLayout ? breadcrumb : undefined}
            titleAlign="center"
          />
        </div>
      </Box>

      <div className={styles.content}>
        {isLoading ? (
          <LoadingSkeleton
            subjectName={subjectName ?? ""}
            ariaLabel={t("revise.reviseByVideos.loadingAria")}
          />
        ) : isError || videos.length === 0 ? (
          <div className={styles.listContainer}>
            <p className={styles.errorState}>
              {isError
                ? t("revise.reviseByVideos.loadError")
                : t("revise.reviseByVideos.empty")}
            </p>
          </div>
        ) : (
          <div className={styles.listContainer}>
            {videos.map((item, index) => (
              <div
                key={item.videoId}
                className={styles.listItem}
                onClick={() => openVideoPlayer(index)}
              >
                <img
                  className={styles.thumbnail}
                  src={item.thumbnailURL}
                  alt={item.title}
                />
                <div className={styles.itemContent}>
                  <p className={styles.itemTitle}>{item.title}</p>
                  <p className={styles.itemMeta}>
                    {formatDurationVerbose(item.duration)}
                    {item.likes ? ` • ${item.likes}` : ""}
                  </p>
                </div>
                <Box style={{ marginLeft: "auto" }}>
                  <Icon name="iconVideoplay" size={20} />
                </Box>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
